// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import type { RialoSignMessageFeature } from "@rialo/wallet-standard";
import type { Wallet } from "@wallet-standard/base";
import type { FrostConfig } from "../config";
import {
  isFrostError,
  UnsupportedFeatureError,
  WalletDisconnectedError,
  WalletError,
  WalletNotFoundError,
} from "../errors";

type WalletWithMessageFeatures = Wallet & {
  features: {
    "rialo:signMessage"?: RialoSignMessageFeature["rialo:signMessage"];
  };
};

export interface SignMessageOptions {
  /** Message to sign (string will be UTF-8 encoded) */
  message: string | Uint8Array;
}

export interface SignMessageResult {
  /** Message signature */
  signature: Uint8Array;
  /** Signed message bytes */
  signedMessage: Uint8Array;
}

/**
 * Signs an arbitrary message.
 *
 * @param config - Frost configuration
 * @param options - Message to sign (string or bytes)
 * @returns Signature and signed message bytes
 * @throws {WalletDisconnectedError} If no wallet connected
 * @throws {UnsupportedFeatureError} If wallet doesn't support signing
 */
export async function signMessage(
  config: FrostConfig,
  options: SignMessageOptions
): Promise<SignMessageResult> {
  const { store } = config;
  const { walletName, accountAddress } = store.state;

  if (!walletName || !accountAddress) {
    throw new WalletDisconnectedError();
  }

  const wallet = store.state._walletRefs.get(walletName) as WalletWithMessageFeatures | undefined;
  if (!wallet) {
    throw new WalletNotFoundError(walletName);
  }

  const signFeature = wallet.features["rialo:signMessage"];
  if (!signFeature) {
    throw new UnsupportedFeatureError("rialo:signMessage", walletName);
  }

  const messageBytes =
    typeof options.message === "string"
      ? new TextEncoder().encode(options.message)
      : options.message;

  // Get the account entity with raw wallet-standard reference
  const account = store.state.accounts.get(accountAddress);
  if (!account?._raw) {
    throw new WalletDisconnectedError();
  }

  try {
    const results = await signFeature.signMessage({
      account: account._raw,
      message: messageBytes,
    });

    store.setState(state => ({
      ...state,
      lastUsedAt: Date.now(),
    }));

    return {
      signature: results[0].signature,
      signedMessage: results[0].signedMessage,
    };
  } catch (error) {
    if (isFrostError(error)) {
      throw error;
    }
    throw new WalletError(error, walletName);
  }
}
